import { useState } from "react";
import AppLayout from "@/components/AppLayout";
import { Button } from "@/components/ui/button";
import { ChangePasswordDialog, DeleteAccountDialog } from "@/components/SecurityDialogs";
import { Lock, AlertTriangle, Trash2, ShieldCheck } from "lucide-react";

export default function Security() {
  const [pwOpen, setPwOpen] = useState(false);
  const [delOpen, setDelOpen] = useState(false);

  return (
    <AppLayout title="Security & account" subtitle="Manage your password and account access">
      <div className="grid lg:grid-cols-3 gap-5">
        <div className="lg:col-span-2 space-y-5">
          <SectionCard icon={Lock} title="Change password" subtitle="Section 1" testid="section-change-password">
            <div className="flex items-center justify-between border border-slate-200 rounded-md p-4" data-testid="row-change-password">
              <div className="flex-1 pr-4">
                <div className="font-medium text-slate-900 text-sm">Update your password</div>
                <div className="text-xs text-slate-500 mt-1">
                  Use a strong password you don't use elsewhere. Updating your password will sign you out of all other devices.
                </div>
              </div>
              <Button variant="outline" onClick={() => setPwOpen(true)} data-testid="btn-open-change-pw">
                <Lock className="w-3.5 h-3.5 mr-1.5" /> Change password
              </Button>
            </div>
          </SectionCard>

          <SectionCard icon={AlertTriangle} title="Delete account" subtitle="Section 2" testid="section-delete-account" danger>
            <div className="flex items-center justify-between border border-red-200 bg-red-50/40 rounded-md p-4" data-testid="row-delete-account">
              <div className="flex-1 pr-4">
                <div className="font-medium text-red-700 text-sm flex items-center gap-1.5">
                  <AlertTriangle className="w-3.5 h-3.5" /> Permanently delete your account
                </div>
                <div className="text-xs text-red-600/80 mt-1">
                  Scrubs your personal data, revokes all sessions, and removes you from active projects. This cannot be undone.
                </div>
              </div>
              <Button onClick={() => setDelOpen(true)} className="bg-red-600 hover:bg-red-700" data-testid="btn-open-delete-account">
                <Trash2 className="w-3.5 h-3.5 mr-1.5" /> Delete account
              </Button>
            </div>
          </SectionCard>
        </div>

        <div className="space-y-4">
          <div className="kpi-card">
            <div className="flex items-center gap-2 mb-2">
              <ShieldCheck className="w-4 h-4 text-emerald-600" />
              <div className="font-display font-bold text-slate-900">Account protection</div>
            </div>
            <ul className="space-y-2 text-sm text-slate-700">
              <li className="flex items-start gap-2">
                <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 mt-1.5 flex-shrink-0" />
                <span>2FA email OTP is <b>enabled</b> for every sign-in.</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 mt-1.5 flex-shrink-0" />
                <span>Brute-force lockout after 5 failed sign-in attempts in 15 minutes.</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 mt-1.5 flex-shrink-0" />
                <span>Password changes revoke all other active sessions instantly.</span>
              </li>
            </ul>
          </div>
        </div>
      </div>

      <ChangePasswordDialog open={pwOpen} onOpenChange={setPwOpen} />
      <DeleteAccountDialog open={delOpen} onOpenChange={setDelOpen} />
    </AppLayout>
  );
}

function SectionCard({ icon: Icon, title, subtitle, children, testid, danger = false }) {
  return (
    <div className="kpi-card" data-testid={testid}>
      <div className="flex items-start gap-3 mb-4">
        <div className={`w-9 h-9 rounded-md ${danger ? "bg-red-600 text-white" : "bg-slate-900 text-cyan-400"} flex items-center justify-center`}>
          <Icon className="w-4 h-4" strokeWidth={1.75} />
        </div>
        <div>
          <h3 className="font-display font-bold text-slate-900">{title}</h3>
          <div className="text-[11px] text-slate-500 uppercase tracking-wider mt-0.5">{subtitle}</div>
        </div>
      </div>
      {children}
    </div>
  );
}
